import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import ReactSelect from "react-select";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import Swal from "sweetalert2";

// Validation schema
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  emailAddress: Yup.string()
    .email("Invalid email")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .matches(/^\d{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  address: Yup.string(),
  city: Yup.string(),
  state: Yup.string(),
  zipCode: Yup.string(),
  fax: Yup.string(),
  npi: Yup.string(),
  taxonomy: Yup.string(),
  ssn: Yup.string(),
  licenseNumber: Yup.string(),
  taxID: Yup.string(),
  type: Yup.string(),
  gender: Yup.string(),
  id: Yup.number().default(0),
  practiceId: Yup.number().default(0),
});

const ProviderRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("Thyra_Token");
  const [practiceId, setCurrentPracticeId] = useState(() => {
    const practiceId = localStorage.getItem("default_practice");
    return practiceId ? parseInt(practiceId, 10) : 0;
  });
  
  const providerId =
    location.state?.providerId ||
    parseInt(localStorage.getItem("providerId")) ||
    0;

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const defaultValues = {
    id: 0,
    practiceId: practiceId || 0,
    firstName: "",
    middleName: "",
    lastName: "",
    phoneNumber: "",
    inactive: false,
    isBillingProvider: true,
    positionTitle: "",
    address: "",
    emailAddress: "",
    zipCode: "",
    city: "",
    title: "",
    dob: "",
    uPIN: "",
    state: "",
    phoneNo: "",
    createdBy: "",
    createdDate: "",
    updatedBy: "",
    updatedDate: "",
    gender: "",
    typeOfLicense: "",
    deAno: "",
    clinical: "",
    hireDate: "",
    billingSupervisor: "",
    clinicalSupervisor: "",
    type: "",
    fax: "",
    taxID: "",
    npi: "",
    licenseNumber: "",
    ssn: "",
    taxonomy: "",
    attendingPhysician: false,
    billingPhysician: false,
  };

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const [practicesList, setPracticesList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpenResp, setDialogOpenResp] = useState(false);

  // Fetch practices list
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/Practices/GetPracticesList`, {
        headers,
      })
      .then((response) => {
        const prac = response.data.map((field) => ({
          label: field.PracticeName,
          value: field.id,
        }));
        setPracticesList(prac);
      })
      .catch((error) => {
        console.error("Error fetching practices:", error);
      });
  }, []);

  // Fetch provider data if providerId exists
  useEffect(() => {
    if (providerId > 0) {
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/Providers/getProviderById?id=${providerId}`,
          { headers }
        )
        .then((response) => {
          const data = response;
          if (!data || typeof data !== "object") {
            throw new Error("Invalid API response");
          }
          const fieldMapping = {
            id: data.id,
            practiceId: data.practiceId,
            firstName: data.firstName,
            middleName: data.middleName || "",
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            inactive: data.inactive,
            isBillingProvider: data.isBillingProvider,
            positionTitle: data.positionTitle || "",
            address: data.address || "",
            emailAddress: data.emailAddress,
            zipCode: data.zipCode || "",
            city: data.city || "",
            state: data.state || "",
            fax: data.fax || "",
            npi: data.npi || "",
            taxonomy: data.taxonomy || "",
            ssn: data.ssn || "",
            licenseNumber: data.licenseNumber || "",
            taxID: data.taxID || "",
            type: data.type || "",
            gender: data.gender || "",
            attendingPhysician: data.attendingPhysician,
            billingPhysician: data.billingPhysician,
          };

          Object.entries(fieldMapping).forEach(([field, value]) => {
            setValue(field, value);
          });
        })
        .catch((error) => {
          console.error(
            "Error fetching provider data:",
            error.response?.data,
            error.response?.status
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch provider data.",
          });
        });
    } else {
      console.log("No valid providerId, skipping API call");
    }
  }, [providerId, setValue]);

  // Handle form submission
  const onSubmit = (data) => {
    if (submitting) return;
    setSubmitting(true);
    // const postData = data;
    const postData = {
      ...data,
      practiceId: data.practiceId || practiceId || 0, // Use form's practiceId
    };
    
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/Providers/addProvider`,
        postData,
        {
          headers,
        }
      )

      .then((response) => {
        if (response.id) {
          setDialogOpenResp(true);
          setTimeout(() => {
            localStorage.removeItem("providerId");
            navigate("/Provider", { state: { providerId: response.id } });
            setDialogOpenResp(false);
          }, 1000);
        }
        setSubmitting(false);
      })
      .catch((error) => {
        console.error(
          "Submission error:",
          error.response?.data,
          error.response?.status
        );
        // Swal.fire({
        //   icon: 'error',
        //   title: 'Error',
        //   text: 'Failed to save provider data.',
        // });
        setSubmitting(false);
      });
  };

  // Handle cancel button
  const handleCancel = () => {
    localStorage.removeItem("providerId");
    navigate("/Provider");
  };

  return (
    <Container sx={{ pt: 10, pb: 4, maxWidth: "lg" }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold", color: "#2B7B7E" }}>
          Provider Registration
        </Typography>
      </Box>
      <Dialog
        open={isDialogOpenResp}
        onClose={() => setDialogOpenResp(false)}
        PaperProps={{
          style: {
            marginRight: "5px",
            float: "right",
            marginTop: "5px",
            color: "red",
            cursor: "pointer",
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: "8px",
            width: "500px",
          }}
        >
          <CheckCircleIcon style={{ fontSize: "100px", color: "green" }} />
          <h5
            style={{
              textAlign: "center",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#444444",
            }}
          >
            Provider {providerId > 0 ? "updated" : "added"} successfully
          </h5>
          <DialogContent />
        </div>
      </Dialog>
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
          p: 4,
        }}
      >
        <div className="row">
          <div className="col somme-12 mb-3">
            <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
            <div className="row">
              <div className="col-md-3">
                <Typography component="label" color="black">
                  First Name
                </Typography>
                <TextField
                  {...register("firstName")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter First Name"
                  style={{ marginTop: "8px" }}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Last Name
                </Typography>
                <TextField
                  {...register("lastName")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Last Name"
                  style={{ marginTop: "8px" }}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Phone Number
                </Typography>
                <TextField
                  {...register("phoneNumber")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Phone Number"
                  style={{ marginTop: "8px" }}
                  onInput={(e) => {
                    const input = e.target;
                    input.value = input.value.replace(/\D/g, "").slice(0, 10);
                  }}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Fax
                </Typography>
                <TextField
                  {...register("fax")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Fax"
                  style={{ marginTop: "8px" }}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: "10px" }}>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Email
                </Typography>
                <TextField
                  {...register("emailAddress")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Email"
                  style={{ marginTop: "8px" }}
                  error={!!errors.emailAddress}
                  helperText={errors.emailAddress?.message}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Address
                </Typography>
                <TextField
                  {...register("address")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Address"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  City
                </Typography>
                <TextField
                  {...register("city")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter City"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  State
                </Typography>
                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      {...field}
                      options={[
                        { value: "AL", label: "ALABAMA" },
                        { value: "AK", label: "ALASKA" },
                        { value: "AS", label: "AMERICAN SAMOA" },
                        { value: "AZ", label: "ARIZONA" },
                      ]}
                      value={
                        getValues("state")
                          ? {
                              label: getValues("state"),
                              value: getValues("state"),
                            }
                          : null
                      }
                      onChange={(selectedOption) =>
                        setValue("state", selectedOption?.value || "")
                      }
                      styles={{
                        control: (base) => ({
                          ...base,
                          marginTop: 8,
                          marginBottom: 0,
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 5000,
                        }),
                      }}
                    />
                  )}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: "10px" }}>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Zip Code
                </Typography>
                <TextField
                  {...register("zipCode")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Zip Code"
                  style={{ marginTop: "8px" }}
                  onInput={(e) => {
                    const input = e.target;
                    input.value = input.value.replace(/\D/g, "").slice(0, 9);
                  }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  NPI
                </Typography>
                <TextField
                  {...register("npi")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter NPI"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  Taxonomy
                </Typography>
                <TextField
                  {...register("taxonomy")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter Taxonomy"
                  style={{ marginTop: "8px" }}
                />
              </div>
              <div className="col-md-3">
                <Typography component="label" color="black">
                  SSN
                </Typography>
                <TextField
                  {...register("ssn")}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Enter SSN"
                  style={{ marginTop: "8px" }}
                />
              </div>
            </div>
            <Grid
              container
              spacing={2}
              style={{ margin: 20, width: "90%", justifyContent: "flex-end" }}
            >
              <Button
                onClick={handleCancel}
                sx={{
                  width: "100px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  borderRadius: "80px",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#1a1a1a" },
                }}
              >
                <CancelOutlinedIcon
                  fontSize="medium"
                  style={{ color: "red", paddingRight: "5px" }}
                />
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={submitting}
                sx={{
                  width: "100px",
                  height: "35px",
                  backgroundColor: "#2B7B7E",
                  color: "white",
                  borderRadius: "80px",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#1a1a1a" },
                }}
              >
                <SaveOutlinedIcon
                  fontSize="medium"
                  style={{
                    color: "white",
                    paddingRight: "5px",
                    paddingBottom: "2px",
                  }}
                />
                Save
              </Button>
            </Grid>
          </div>
        </div>
      </Box>
    </Container>
  );
};

export default ProviderRegistration;
